import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { APIService } from 'src/api/api';
import { getStoredUserTimezone } from 'src/utils/timezoneUtils';

const DATE_TIME_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

const getInitialDateRange = () => {
  const now = moment().tz(getStoredUserTimezone()).seconds(0).milliseconds(0);
  return {
    from: now.clone().startOf('day').format(DATE_TIME_INPUT_FORMAT),
    to: now.format(DATE_TIME_INPUT_FORMAT)
  };
};

const toUnixSeconds = (value, timezoneName) => {
  const parsed = moment.tz(value, DATE_TIME_INPUT_FORMAT, true, timezoneName);
  return parsed.isValid() ? parsed.unix() : null;
};

const isUnavailableMetric = (value) => {
  if (value === null || value === undefined || value === '') return true;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && (parsedValue === -10000 || parsedValue === -1);
};

const formatDecimalValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '--';
  return parsedValue.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatTenthsValue = (value, decimals = 1) => {
  if (isUnavailableMetric(value)) return '--';
  return formatDecimalValue(Number(value) / 10, decimals);
};

const formatHhMmSs = (value) => {
  if (isUnavailableMetric(value)) return '--';
  const totalSeconds = Math.max(0, Math.floor(Number(value)));
  if (!Number.isFinite(totalSeconds)) return '--';
  return `${Math.floor(totalSeconds / 3600)}:${String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

const formatDurationWithPercent = (seconds, percent) => {
  const duration = formatHhMmSs(seconds);
  if (duration === '--') return '--';
  const percentage = formatDecimalValue(percent, 1);
  return percentage === '--' ? duration : `${duration} (${percentage} %)`;
};

const normalizeStatistics = (response) => {
  const root = response?.result || response || {};
  const periodMileage = root?.cmw || null;
  const newRoot = root?.results?.data?.[0] || null;
  const legacyRoot = root?.data || {};

  if (newRoot && Object.keys(newRoot).length) {
    const vehicleData = newRoot?.vehicleDataList?.[0] || null;
    return {
      hasData: Boolean(vehicleData && Object.keys(vehicleData).length) || Boolean(Object.keys(newRoot).length),
      vehicleData,
      totals: newRoot,
      periodMileage: periodMileage || newRoot?.cmw || null
    };
  }

  const vehicleData = legacyRoot?.vehicleDataList?.[0] || null;
  const totals = legacyRoot?.totalFuel || null;
  return {
    hasData: Boolean(vehicleData && Object.keys(vehicleData).length) || Boolean(totals && Object.keys(totals).length),
    vehicleData,
    totals,
    periodMileage: periodMileage || vehicleData?.cmw || legacyRoot?.cmw || null
  };
};

const StatisticsSection = ({ title, rows }) => (
  <Card
    sx={{
      p: 1.45, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.16)', boxShadow: 'none', height: '100%'
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
      {title}
    </Typography>
    <Grid container spacing={0.8}>
      {rows.map(([label, value]) => (
        <Grid item xs={12} sm={6} key={`${title}-${label}`}>
          <Box
            sx={{
              p: 0.85, borderRadius: 1.8, border: '1px solid rgba(148, 163, 184, 0.14)', bgcolor: '#FFFFFF', minHeight: 74
            }}
          >
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, lineHeight: 1.28 }}>
              {label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.45, color: '#0F172A', fontWeight: 800, lineHeight: 1.32
              }}
            >
              {value}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Card>
);

StatisticsSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)).isRequired
};

const TankStatistics = ({ tank }) => {
  const timezoneName = getStoredUserTimezone();
  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [appliedRange, setAppliedRange] = useState(getInitialDateRange);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const isEnabled = Boolean(tank?.fuel_graph_enabled);

  const fetchStatistics = useCallback(async () => {
    if (!tank?.id || !isEnabled) return;
    const timeBegin = toUnixSeconds(appliedRange.from, timezoneName);
    const timeEnd = toUnixSeconds(appliedRange.to, timezoneName);
    if (timeBegin === null || timeEnd === null || timeBegin >= timeEnd) return;

    setLoading(true);
    setError('');
    try {
      const response = await APIService.getTankStatistics(tank.id, {
        timeBegin,
        timeEnd,
        timezone: timezoneName,
        tz: timezoneName
      });
      if (!response?.success) throw response;
      const normalized = normalizeStatistics(response);
      setStatistics(normalized.hasData ? normalized : null);
      if (!normalized.hasData) setError('No statistics found for the selected range.');
    } catch (requestError) {
      setStatistics(null);
      setError(requestError?.data?.error_message || requestError?.error_message || 'Unable to load tank statistics.');
    } finally {
      setLoading(false);
    }
  }, [appliedRange, isEnabled, tank?.id, timezoneName]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleDateRangeChange = (field) => (event) => {
    setDateRange((current) => ({ ...current, [field]: event.target.value }));
    setValidationError('');
  };

  const handleApply = () => {
    const timeBegin = toUnixSeconds(dateRange.from, timezoneName);
    const timeEnd = toUnixSeconds(dateRange.to, timezoneName);
    if (timeBegin === null || timeEnd === null) {
      setValidationError('Enter a valid start and end date-time.');
      return;
    }
    if (timeBegin >= timeEnd) {
      setValidationError('End date-time must be after start date-time.');
      return;
    }
    setValidationError('');
    setAppliedRange(dateRange);
  };

  if (!isEnabled) {
    return <Alert severity="info">Statistics are available after enabling a supported Omnicomm provider and storing the tank unit id.</Alert>;
  }

  const vehicleStats = statistics?.vehicleData || {};
  const fuelStats = vehicleStats?.fuel || vehicleStats;
  const totalsStats = statistics?.totals || {};
  const periodMileageStats = statistics?.periodMileage || vehicleStats?.cmw || totalsStats?.cmw || {};
  const secondaryEngineStats = Array.isArray(vehicleStats?.uniDataList) ? vehicleStats.uniDataList[0] : null;
  const fromMoment = moment(dateRange.from, DATE_TIME_INPUT_FORMAT, true);
  const toMoment = moment(dateRange.to, DATE_TIME_INPUT_FORMAT, true);
  const selectedRangeLabel = fromMoment.isValid() && toMoment.isValid()
    ? `${fromMoment.format('DD MMM YYYY, hh:mm A')} to ${toMoment.format('DD MMM YYYY, hh:mm A')}`
    : 'Custom range';

  const movementRows = [
    ['Mileage, km', formatDecimalValue(totalsStats.averageMileage ?? vehicleStats.mileage, 2)],
    ['Total mileage at the beginning of the period, km', formatDecimalValue(periodMileageStats.mileageAtPeriodBegin, 2)],
    ['Total mileage at the end of the period, km', formatDecimalValue(periodMileageStats.mileageAtPeriodEnd, 2)],
    ['Engine operation time, hh:mm:ss', formatDurationWithPercent(totalsStats.totalWorked ?? vehicleStats.worked, totalsStats.totalWorkedPercent ?? vehicleStats.workedPercent)],
    ['Engine off time, hh:mm:ss', formatDurationWithPercent(totalsStats.totalLayUpTime ?? vehicleStats.layUp, totalsStats.totalLayUpTimePercent ?? vehicleStats.layUpPercent)],
    ['Maximum speed, km/h', formatDecimalValue(totalsStats.maxSpeed ?? vehicleStats.maxSpeed, 1)]
  ];
  const fuelRows = [
    ['Initial volume, l', formatTenthsValue(fuelStats.startVolume)],
    ['Refueling volume, l', formatTenthsValue(totalsStats.totalRefuelling ?? fuelStats.refuelling)],
    ['Actual mileage per 1 l, km', formatDecimalValue(totalsStats.totalMileagePerLitre ?? vehicleStats.mileagePerLitre, 2)],
    ['Final volume, l', formatTenthsValue(fuelStats.endVolume)],
    ['Draining volume, l', formatTenthsValue(totalsStats.totalDraining ?? fuelStats.draining)],
    ['Fuel consumption per engine hour, l', formatTenthsValue(totalsStats.consPerMHEngineOn ?? vehicleStats.fuelConsPerMotorhour)],
    ['Actual consumption, l', formatTenthsValue(totalsStats.totalConsumption ?? fuelStats.fuelConsumption)]
  ];
  const secondaryRows = [
    ['Operation time, hh:mm:ss', formatDurationWithPercent(secondaryEngineStats?.univInputOnTime, secondaryEngineStats?.univInputOnTimePercent)],
    ['Consumption with auxiliary equipment ON, l', formatTenthsValue(secondaryEngineStats?.univInputOnConsumption)],
    ['Consumption with auxiliary equipment ON per engine hour, l', formatTenthsValue(secondaryEngineStats?.univInputHourConsumption)],
    ['Downtime, hh:mm:ss', formatDurationWithPercent(secondaryEngineStats?.univInputOffTime, secondaryEngineStats?.univInputOffTimePercent)]
  ];

  let reportContent = null;
  if (loading) {
    reportContent = (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading Omnicomm statistics</Typography>
      </Box>
    );
  } else if (statistics) {
    reportContent = (
      <Stack spacing={1.25}>
        <Card
          sx={{
            p: 1.2, borderRadius: 3, border: '1px solid rgba(148, 163, 184, 0.18)', boxShadow: 'none', bgcolor: '#F8FAFC'
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>{tank?.name || 'Tank'}</Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>{selectedRangeLabel}</Typography>
          </Stack>
        </Card>
        <Grid container spacing={1}>
          <Grid item xs={12} lg={6}><StatisticsSection title="Movement and operation" rows={movementRows} /></Grid>
          <Grid item xs={12} lg={6}><StatisticsSection title="Fuel (tank)" rows={fuelRows} /></Grid>
          <Grid item xs={12}><StatisticsSection title="Activity Secondary Engine" rows={secondaryRows} /></Grid>
        </Grid>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'flex-end' }} justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight={800}>Statistics</Typography>
          <Typography variant="body2" color="text.secondary">Omnicomm statistics for the selected tank period.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
          <TextField label="From" type="datetime-local" value={dateRange.from} onChange={handleDateRangeChange('from')} size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="datetime-local" value={dateRange.to} onChange={handleDateRangeChange('to')} size="small" InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={handleApply} sx={{ height: 40, textTransform: 'none', fontWeight: 700 }}>Apply</Button>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.secondary">{`Times shown in ${timezoneName}.`}</Typography>
      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {error && <Alert severity={statistics ? 'warning' : 'error'}>{error}</Alert>}

      {reportContent}
    </Stack>
  );
};

TankStatistics.propTypes = {
  tank: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    fuel_graph_enabled: PropTypes.bool
  })
};

export default TankStatistics;
